// Settings and its pages. Pages that moved to their feature still land there from the old addresses.
import { AUTOMATION_KINDS } from "@steward/shared";
import { createRoute, Navigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ClaudeSettings } from "../../screens/ConnectClaude";
import { AutomationPage, AutomationsSettings } from "../../screens/settings/Automations";
import { DataSettings } from "../../screens/settings/Data";
import { DeletedSettings } from "../../screens/settings/Deleted";
import { SettingsLayout, SettingsOverview } from "../../screens/settings/Layout";
import { AlertsSettings } from "../../screens/settings/More";
import { PlanSettings } from "../../screens/settings/Plan";
import { BusinessInfo, KeysSettings, MessagesSettings, SecuritySettings } from "../../screens/settings/Settings";
import { Allowed, app, Moved } from "./base";

const settings = createRoute({ getParentRoute: () => app, path: "/settings", component: SettingsLayout });

const setting = <P extends string>(path: P, component: () => ReactNode) =>
  createRoute({ getParentRoute: () => settings, path, component });

const automationPage = createRoute({
  getParentRoute: () => settings,
  path: "/automations/$kind",
  component: function AutomationRoute(): ReactNode {
    const { kind } = automationPage.useParams();
    const k = AUTOMATION_KINDS.find((x) => x === kind);
    return k ? (
      <Allowed action="settings.read">
        <AutomationPage kind={k} />
      </Allowed>
    ) : (
      <Navigate to="/settings/automations" />
    );
  },
});

export const settingsTree = settings.addChildren([
  setting("/", SettingsOverview),
  setting("/business", BusinessInfo),
  setting("/money", () => <Moved to="/invoices/settings" />),
  setting("/team", () => <Moved to="/hr/settings" />),
  setting("/alerts", () => (
    <Allowed action="settings.read">
      <AlertsSettings />
    </Allowed>
  )),
  setting("/marketing", () => <Moved to="/marketing/settings" />),
  setting("/prices", () => <Moved to="/quotes/settings" />),
  setting("/eunice", () => <Moved to="/receptionist" />),
  setting("/schedule", () => <Moved to="/schedule/settings" />),
  setting("/website", () => <Moved to="/requests/settings" />),
  setting("/automations", () => (
    <Allowed action="settings.read">
      <AutomationsSettings />
    </Allowed>
  )),
  automationPage,
  setting("/messages", () => (
    <Allowed action="outbox.read">
      <MessagesSettings />
    </Allowed>
  )),
  setting("/data", () => (
    <Allowed action="client.bulk">
      <DataSettings />
    </Allowed>
  )),
  setting("/deleted", () => (
    <Allowed action="client.update">
      <DeletedSettings />
    </Allowed>
  )),
  setting("/keys", () => (
    <Allowed action="keys.read">
      <KeysSettings />
    </Allowed>
  )),
  setting("/plan", () => (
    <Allowed action="franchise.read">
      <PlanSettings />
    </Allowed>
  )),
  setting("/security", SecuritySettings),
  setting("/claude", () => (
    <Allowed action="insights.read">
      <ClaudeSettings />
    </Allowed>
  )),
  setting("/activity", () => <Navigate to="/settings/security" />),
]);
